import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { useAuth } from "../../contexts/Auth";
import "./SignIn.scss";
import logo from "./logoTa.png";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useMedia } from "react-media";
import appleicon from "./Apple.png";
import googleicon from "./Google.png";
import windowsicon from "./Microsoft.png";
import { IconButton, Tooltip } from "@material-ui/core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
const url =
  process.env["REACT_APP_RELEASE_STAGE"] === "staging"
    ? "https://stag-frontend.tradealgo.com"
    : "https://tradealgo.com";
const mediaQuery = {
  isMobile: "screen and (max-width: 991px)",
};

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Detect if we're in iframe mode (embedded in another app)
  const isIframeMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("iframe") === "true";
  }, [location.search]);

  // Store iframe mode in localStorage so auth provider can access it
  useEffect(() => {
    if (isIframeMode) {
      localStorage.setItem("login_iframe_mode", "true");
    } else {
      localStorage.removeItem("login_iframe_mode");
    }
  }, [isIframeMode]);

  // Capture redirect parameter from URL and store it for post-login redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get("redirect");
    if (redirectUrl) {
      // Validate that the redirect URL is within tradealgo.com domain for security
      try {
        const url = new URL(redirectUrl);
        if (url.hostname.endsWith("tradealgo.com")) {
          localStorage.setItem("post_login_redirect", redirectUrl);
        }
      } catch (e) {
        // Invalid URL, ignore
        console.warn("Invalid redirect URL:", redirectUrl);
      }
    }
  }, [location.search]);

  const { isMobile } = useMedia({
    queries: mediaQuery,
  });
  const { signIn, signInLoading, signInWithProvider } = useAuth();
  const [values, setValues] = useState({
    email: "",
    password: "",
  });
  function onSubmit() {
    signIn(values);
  }
  const onKeyDownHandler = (e) => {
    if (e.keyCode === 13) {
      onSubmit();
    }
  };
  const DividerWithText = ({ text }) => (
    <div className="divider-container">
      <div className="divider-border" />
      <span className="divider-text">{text}</span>
      <div className="divider-border" />
    </div>
  );

  return (
    <>
      {/* Hide external header logo when in iframe mode */}
      {!isIframeMode && (
        <a style={{ cursor: "pointer" }} href={url}>
          <img className="logo-ta" alt="" height={36} src={logo} />
        </a>
      )}
      <div className={`sign_in__page${isIframeMode ? " iframe-mode" : ""}`}>
        <div className="right-part">
          <div className="logo-container">
            <img src={logo} alt="Logo" className="logo-image" />
          </div>
          <div className="big-caption">
            {isIframeMode ? "Welcome To TradeAlgo" : "Log In"}
          </div>
          <div className={"contact-form"}>
            <div className={"input-form"}>
              <div className={"input-rows"}>
                <form onKeyDown={onKeyDownHandler} onSubmit={onSubmit}>
                  <div className={"input-row"}>
                    <input
                      value={values.email}
                      onChange={(e) =>
                        setValues({
                          ...values,
                          email: e.target.value.replace(/\s+/g, ""),
                        })
                      }
                      autoComplete="username"
                      type={"text"}
                      className={"input-field full-width"}
                      placeholder={"E-mail"}
                    />
                  </div>
                  <div className="input-row">
                    <div className="input-container">
                      <input
                        value={values.password}
                        onChange={(e) =>
                          setValues({
                            ...values,
                            password: e.target.value,
                          })
                        }
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        className="input-field full-width"
                        placeholder="Password"
                      />
                      <span
                        onClick={togglePasswordVisibility}
                        className="password-toggle-icon"
                        aria-label="Toggle password visibility"
                      >
                        <Tooltip
                          placement="top"
                          title={(showPassword ? "Hide" : "Show") + " Password"}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon sx={{ width: 29, height: 29 }} />
                          ) : (
                            <VisibilityIcon sx={{ width: 29, height: 29 }} />
                          )}
                        </Tooltip>
                      </span>
                    </div>
                  </div>
                  <div className={isMobile ? "input-row2" : "input-row"}>
                    <div className={isMobile ? "squaredThree" : "squaredTwo"}>
                      <input
                        type="checkbox"
                        className={"remember-me-checkbox"}
                        id="rememberMe"
                        name="rememberMe"
                      />
                      <label htmlFor="rememberMe" className={"mb-0"}>
                        Remember me
                      </label>
                    </div>
                    <Link
                      to={`/reset-password${values.email.length > 0 ? `?email=${values.email}` : ""}`}
                      style={{ marginBottom: isMobile ? "10px" : "0px" }}
                    >
                      <span className={"forgot-password"}>
                        Forgot Password?
                      </span>
                    </Link>
                  </div>
                  <div className={"input-row2"}>
                    <Button
                      label="Log in"
                      className="ml-0 full-width btn-primary sign-in-button"
                      labelClassname="text-bold text-uppercase"
                      onClick={onSubmit}
                      disabled={signInLoading}
                    >
                      {signInLoading ? (
                        <CircularProgress color="inherit" size={24} />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                  {/* Hide "Back to Homepage" when in iframe mode */}
                  {!isIframeMode && (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <a style={{ textAlign: "center" }} href={url}>
                        <KeyboardBackspaceIcon />
                        &nbsp;&nbsp; Back To Homepage{" "}
                      </a>
                    </div>
                  )}
                </form>
                <DividerWithText text="Or log in with" />
                <div className="social-container">
                  <IconButton
                    onClick={() => signInWithProvider("google")}
                    aria-label="Log in with Google"
                  >
                    <img src={googleicon} alt="Log in with Google" />
                  </IconButton>
                  <IconButton
                    onClick={() => signInWithProvider("microsoft")}
                    className="!relative !top[-1px]"
                    aria-label="Log in with Microsoft"
                  >
                    <img src={windowsicon} alt="Log in with Microsoft" />
                  </IconButton>
                  <IconButton
                    onClick={() => signInWithProvider("apple")}
                    aria-label="Log in with Apple"
                  >
                    <img src={appleicon} alt="Log in with Apple" />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
